'use client';

import React, { useEffect, useRef, useState } from 'react';

export type PreviewDevice = 'desktop' | 'tablet' | 'mobile';
export type PreviewZoom = 'fit' | 0.5 | 0.75 | 1;

const FRAME_WIDTH: Record<PreviewDevice, number | null> = {
  desktop: null,
  tablet: 768,
  mobile: 375,
};

interface PreviewFrameProps {
  device: PreviewDevice;
  zoom: PreviewZoom;
  children: React.ReactNode;
  className?: string;
}

/**
 * Device-framed canvas with auto-fit scaling. When the parent is narrower than
 * the device width, the frame scales from the top center so nothing clips and
 * horizontal scrollbars stay gone.
 */
export function PreviewFrame({ device, zoom, children, className = '' }: PreviewFrameProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [hostWidth, setHostWidth] = useState(0);

  useEffect(() => {
    const node = hostRef.current;
    if (!node) return;
    const measure = () => setHostWidth(node.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const frameWidth = FRAME_WIDTH[device];
  const available = Math.max(hostWidth - 32, 0);
  const natural = frameWidth ?? Math.max(available || 960, 320);
  const fitScale = frameWidth && available > 0 ? Math.min(1, available / frameWidth) : 1;
  const requested = zoom === 'fit' ? fitScale : zoom;
  const effectiveScale = frameWidth ? Math.min(requested, fitScale || requested) : requested;

  return (
    <div
      ref={hostRef}
      className={`scrollbar-none relative flex h-full min-h-0 flex-1 justify-center overflow-x-hidden overflow-y-auto bg-shell/40 p-4 ${className}`}
    >
      <div
        className="relative shrink-0"
        style={{
          width: natural * effectiveScale,
          height: 'auto',
        }}
      >
        <div
          style={{
            width: natural,
            transform: `scale(${effectiveScale})`,
            transformOrigin: 'top left',
          }}
        >
          <div
            className={`overflow-hidden bg-white shadow-xl ring-1 ring-line/60 ${
              device === 'mobile'
                ? 'rounded-[1.75rem] ring-[10px] ring-slate-900'
                : device === 'tablet'
                  ? 'rounded-2xl ring-[6px] ring-slate-800'
                  : 'rounded-xl'
            }`}
          >
            {device === 'mobile' && (
              <div className="flex h-6 items-center justify-center bg-slate-900">
                <span className="h-1.5 w-16 rounded-full bg-slate-700" aria-hidden />
              </div>
            )}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export const ZOOM_PRESETS: { id: PreviewZoom; label: string }[] = [
  { id: 'fit', label: 'Fit' },
  { id: 0.5, label: '50%' },
  { id: 0.75, label: '75%' },
  { id: 1, label: '100%' },
];
